import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, of, pipe } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { Participation } from 'app/exercise/shared/entities/participation/participation.model';
import { Result } from 'app/exercise/shared/entities/result/result.model';
import { Exercise, ExerciseType } from 'app/exercise/shared/entities/exercise/exercise.model';
import { StudentParticipation } from 'app/exercise/shared/entities/participation/student-participation.model';
import { ParticipationService } from 'app/exercise/participation/participation.service';
import { WebsocketService } from 'app/foundation/service/websocket.service';
import dayjs from 'dayjs/esm';
import { ProgrammingExercise } from 'app/programming/shared/entities/programming-exercise.model';
import { Submission } from 'app/exercise/shared/entities/submission/submission.model';
import { deepClone } from 'app/foundation/util/deep-clone.util';
import { SubmissionUpdateResult } from 'app/exercise/shared/entities/submission/submission-updated-with-result.model';
import { AccountService } from 'app/core/auth/account.service';
import { AssessmentType } from 'app/assessment/shared/entities/assessment-type.model';

/**
 * Websocket destination for user-specific participation results.
 */
const PERSONAL_PARTICIPATION_TOPIC = `/user/topic/newResults`;

/**
 * Constructs the websocket destination for exercise-wide participation results.
 *
 * @param exerciseId ID of the exercise
 * @returns topic URL for new results of the given exercise
 */
const EXERCISE_PARTICIPATION_TOPIC = (exerciseId: number) => `/topic/exercise/${exerciseId}/newResults`;

/**
 * Public API of the ParticipationWebsocketService.
 * Exposed for dependency inversion and easier mocking in tests.
 */
export interface IParticipationWebsocketService {
    /**
     * Adds a participation to the local cache and notifies subscribers.
     *
     * @param participation The participation to be cached
     * @param exercise Optional exercise object if it is not contained in the participation
     */
    addParticipation: (participation: Participation, exercise?: Exercise) => void;

    /**
     * Returns all cached student participations for a given exercise.
     *
     * @param exerciseId ID of the exercise
     * @returns Array of student participations for the exercise or undefined if nothing is cached
     */
    getParticipationsForExercise: (exerciseId: number) => StudentParticipation[] | undefined;

    /**
     * Subscribes to general changes of participations.
     * The emitted values are full participation objects including results and exercise.
     *
     * @returns A BehaviorSubject that emits participation updates
     */
    subscribeForParticipationChanges: () => BehaviorSubject<Participation | undefined>;

    /**
     * Subscribes to the latest result for a specific participation.
     * Opens websocket subscriptions as needed.
     *
     * @param participationId ID of the participation
     * @param personal Whether this subscription is personal (user-specific) or exercise-wide
     * @param exerciseId Optional ID of the exercise (required when personal is false)
     * @returns A BehaviorSubject that emits the latest result or undefined initially
     */
    subscribeForLatestResultOfParticipation: (participationId: number, personal: boolean, exerciseId?: number) => BehaviorSubject<Result | undefined>;

    /**
     * Unsubscribes from tracking the latest result of a participation.
     * Potentially closes websocket connections if no longer needed.
     *
     * @param participationId ID of the participation to unsubscribe
     * @param exercise Exercise that the participation belongs to
     */
    unsubscribeForLatestResultOfParticipation: (participationId: number, exercise: Exercise) => void;

    /**
     * Notifies all result subscribers with the given result and updates the cached participation.
     *
     * @param result The new result
     */
    notifyAllResultSubscribers: (result: Result) => void;
}

@Injectable({ providedIn: 'root' })
export class ParticipationWebsocketService implements IParticipationWebsocketService, OnDestroy {
    private websocketService = inject(WebsocketService);
    private participationService = inject(ParticipationService);
    private readonly accountService = inject(AccountService);

    private currentUserId?: number;
    private authenticationStateSubscription: Subscription;

    constructor() {
        this.currentUserId = this.accountService.userIdentity()?.id;
        this.authenticationStateSubscription = this.accountService.getAuthenticationState().subscribe((user) => {
            if (this.currentUserId !== user?.id) {
                this.currentUserId = user?.id;
                this.resetStateOnUserChange();
            }
        });
    }

    ngOnDestroy(): void {
        this.authenticationStateSubscription?.unsubscribe();
    }

    /**
     * Tears down all participation state and websocket subscriptions on logout / user change so the
     * next user does not inherit the previous user's cached participations or live result streams.
     *
     * We don't reuse {@link resetLocalCache} here because that path keys off cached participations:
     * it would miss the personal websocket subscription if no participations were cached, and it
     * silently abandons existing result/participation subjects without completing them — leaving
     * any lingering subscribers stuck on a stale stream.
     */
    private resetStateOnUserChange(): void {
        this.openPersonalWebsocketSubscription?.unsubscribe();
        this.openPersonalWebsocketSubscription = undefined;
        this.openResultWebsocketSubscriptions.forEach((subscription) => subscription.unsubscribe());
        this.openResultWebsocketSubscriptions.clear();
        this.resultObservables.forEach((subject) => subject.complete());
        this.resultObservables.clear();
        this.participationObservable?.complete();
        this.participationObservable = undefined;
        this.cachedParticipations.clear();
        this.subscribedExercises.clear();
        this.participationSubscriptionTypes.clear();
    }

    /**
     * Cache of student participations by participationId.
     */
    cachedParticipations: Map<number, StudentParticipation> = new Map<number, StudentParticipation>();

    /**
     * Tracks open non-personal result websocket subscriptions by exerciseId.
     * Value is the websocket subscription.
     */
    openResultWebsocketSubscriptions: Map<number, Subscription> = new Map<number, Subscription>();

    /**
     * Subscription of one open personal websocket subscription if any.
     */
    openPersonalWebsocketSubscription?: Subscription;

    /**
     * BehaviorSubjects that emit the latest result per participationId.
     */
    resultObservables: Map<number, BehaviorSubject<Result | undefined>> = new Map<number, BehaviorSubject<Result | undefined>>();

    /**
     * BehaviorSubject that emits the latest updated participation.
     * Created lazily when a first subscription is requested.
     */
    participationObservable?: BehaviorSubject<Participation | undefined>;

    /**
     * Mapping of exercise IDs to the set of participation IDs that are currently subscribed for that exercise.
     */
    subscribedExercises: Map<number /* ID of exercise */, Set<number> /* IDs of the participations of this exercise */> = new Map<number, Set<number>>();

    /**
     * Tracks subscription type per participation ID.
     * Value is true for personal subscriptions and false for exercise-wide subscriptions.
     */
    participationSubscriptionTypes: Map<number /* ID of participation */, boolean /* Whether the participation was subscribed in personal mode */> = new Map<number, boolean>();

    /**
     * Central pipe for handling incoming results from the websocket.
     *
     * The server only sends Result objects, but callers consume them in two ways:
     *  - via per-participation "latest result" streams (can be read as-is), and
     *  - via Participation objects (e.g. in course exercise details/result history component).
     *  - participation objects need to be updated with the latest results wherever the participation object
     *  is used to display the latest results
     *
     * To keep both views in sync, each incoming result is processed as follows:
     *  1. Notify result subscribers,
     *  2. Update the cached participation's submissions
     *  3. Read the updated participation from the cache
     *  4. Notify participation subscribers.
     *
     * @returns A pipeable operator that applies this flow to each websocket result.
     */
    private getNotifyAllSubscribersPipe = () => {
        return pipe(
            tap(this.notifyResultSubscribers),
            tap(this.updateCachedParticipationWithResult),
            switchMap(this.getParticipationForResult),
            tap(this.notifyParticipationSubscribers),
        );
    };

    /**
     * Removes all locally cached participations and resets all related state.
     * Also cleans up all websocket subscription tracking data.
     */
    public resetLocalCache() {
        const participations = this.getAllParticipations();
        participations.forEach((participation) => {
            this.cachedParticipations.delete(participation.id!);
            this.removeParticipation(participation.id!, participation.exercise?.id);
        });
        this.cachedParticipations = new Map<number, StudentParticipation>();
        this.resultObservables = new Map<number, BehaviorSubject<Result | undefined>>();
        this.participationObservable = undefined;
        this.subscribedExercises = new Map<number, Set<number>>();
        this.participationSubscriptionTypes = new Map<number, boolean>();
    }

    /**
     * Notify all participation subscribers with the newest participation value (e.g. if the result has changed).
     *
     * @param participation Updated participation object
     */
    private notifyParticipationSubscribers = (participation: Participation | undefined): void => {
        if (!this.participationObservable) {
            this.participationObservable = new BehaviorSubject<Participation | undefined>(participation);
        } else {
            this.participationObservable.next(participation);
        }
    };

    /**
     * Notify all result subscribers with the newest result provided.
     *
     * @param result Newly received result
     */
    private notifyResultSubscribers = (result: Result): void => {
        const resultObservable = this.resultObservables.get(result.submission!.participation!.id!);
        // TODO: We never convert the date strings of the result (e.g. completionDate) to a Dayjs object
        //  this could be an issue in some parts of app when a formatted date is needed.
        if (!resultObservable) {
            this.resultObservables.set(result.submission!.participation!.id!, new BehaviorSubject<Result | undefined>(result));
        } else {
            resultObservable.next(result);
        }
    };

    /**
     * Emits the participation associated with the given result from the cache, if present.
     * This method does not mutate the cached participation, it only returns it as an observable.
     *
     * @param result Result whose participation should be retrieved from cache
     * @returns Observable emitting the cached participation or an empty observable if none is found
     */
    private getParticipationForResult = (result: Result): Observable<StudentParticipation | undefined> => {
        const cachedParticipation = this.cachedParticipations.get(result.submission!.participation!.id!);
        if (cachedParticipation) {
            return of(this.cachedParticipations.get(result.submission!.participation!.id!));
        }
        return of();
    };

    /**
     * This adds a participation to the cached data maps. The exercise information is required to find the correct
     * participations for a given exercise. Please note: we explicitly do not want to use websockets here!
     *
     * @param newParticipation The new participation for the cached data maps
     * @param exercise (optional) The exercise that the participation belongs to. Only needed if exercise is missing in participation.
     * @throws Error if neither the participation nor the parameter provide a link to the exercise
     */
    public addParticipation = (newParticipation: StudentParticipation, exercise?: Exercise) => {
        // The participation needs to be cloned so that the original object is not modified
        const participation = deepClone(newParticipation);
        if (!participation.exercise && !exercise) {
            throw new Error('a link from the participation to the exercise is required. Please attach it manually or add exercise as function input');
        }
        participation.exercise = participation.exercise || exercise;
        this.cachedParticipations.set(participation.id!, participation);
        this.notifyParticipationSubscribers(participation);
    };

    /**
     * Returns all participations for all exercises.
     * The participation objects include the exercise data and all results.
     *
     * @returns Array of all cached student participations
     */
    private getAllParticipations(): StudentParticipation[] {
        return [...this.cachedParticipations.values()];
    }

    /**
     * Returns the student participation for the given exercise.
     * The participation objects include the exercise data and all results.
     *
     * @param exerciseId ID of the exercise that the participations belong to.
     * @returns The cached student participations (merged by test run and normal participation)
     *          for the exercise or an empty array
     */
    public getParticipationsForExercise(exerciseId: number): StudentParticipation[] {
        const participationsForExercise = [...this.cachedParticipations.values()].filter((participation) => {
            return participation.exercise?.id === exerciseId;
        });
        if (participationsForExercise?.length) {
            return this.participationService.mergeStudentParticipations(participationsForExercise);
        } else {
            return [];
        }
    }

    /**
     * Unsubscribes from the topics used by the participationId, if possible.
     * This may close personal or exercise-wide websocket connections if they are no longer needed.
     *
     * @param participationId ID of the participation that should not be tracked anymore
     * @param exerciseId Optional ID of the exercise whose subscriptions may be cleaned up
     */
    private removeParticipation(participationId: number, exerciseId?: number) {
        const subscriptionTypePersonal = this.participationSubscriptionTypes.get(participationId);
        this.participationSubscriptionTypes.delete(participationId);

        // We are only interested if there is a value
        if (subscriptionTypePersonal != undefined) {
            if (subscriptionTypePersonal) {
                // The subscription was a personal subscription, so it should only be removed if it was the last of it kind
                const openPersonalSubscriptions = [...this.participationSubscriptionTypes.values()].filter((personal: boolean) => personal).length;
                if (openPersonalSubscriptions === 0) {
                    this.openPersonalWebsocketSubscription?.unsubscribe();
                    this.openPersonalWebsocketSubscription = undefined;
                }
            } else {
                // The subscriptions are non-personal subscriptions, so it should only be removed if it was the last for this exercise
                const openSubscriptionsForExercise = this.subscribedExercises.get(exerciseId!);
                if (openSubscriptionsForExercise) {
                    openSubscriptionsForExercise.delete(participationId);
                    if (openSubscriptionsForExercise.size === 0) {
                        this.subscribedExercises.delete(exerciseId!);
                        const subscription = this.openResultWebsocketSubscriptions.get(exerciseId!);
                        if (subscription) {
                            subscription.unsubscribe();
                            this.openResultWebsocketSubscriptions.delete(exerciseId!);
                        }
                    }
                }
            }
        }
    }

    /**
     * Checks if a websocket connection for new results to the server already exists.
     * If not, a new one will be opened and the participation will be registered as subscribed.
     *
     * @param participationId The ID of the participation for which the subscription should be opened
     * @param personal Whether the current user is a participant in the participation
     * @param exerciseId Optional exerciseId of the exercise where the participation is part of,
     *                   only needed if personal == false
     */
    private openResultWebsocketSubscriptionIfNotExisting(participationId: number, personal: boolean, exerciseId?: number) {
        if ((personal && !this.openPersonalWebsocketSubscription) || (!personal && !this.openResultWebsocketSubscriptions.has(exerciseId!))) {
            let participationResultTopic: string;
            if (personal) {
                participationResultTopic = PERSONAL_PARTICIPATION_TOPIC;
            } else {
                participationResultTopic = EXERCISE_PARTICIPATION_TOPIC(exerciseId!);
            }
            this.participationSubscriptionTypes.set(participationId, personal);
            if (!this.subscribedExercises.has(exerciseId!)) {
                this.subscribedExercises.set(exerciseId!, new Set<number>());
            }
            const subscribedParticipations = this.subscribedExercises.get(exerciseId!);
            subscribedParticipations!.add(participationId);

            const subscription = this.websocketService.subscribe<Result>(participationResultTopic).pipe(this.getNotifyAllSubscribersPipe()).subscribe();
            if (personal) {
                this.openPersonalWebsocketSubscription = subscription;
            } else {
                this.openResultWebsocketSubscriptions.set(exerciseId!, subscription);
            }
        }
    }

    /**
     * Updates the cached participation for the given result.
     *
     * - Looks up the participation in the local cache by result.submission.participation.id.
     * - If a matching submission exists, its `results` array is updated by replacing/adding this result.
     * - If no matching submission exists, a submission is created from the result and appended.
     * - Finally, writes the updated participation back into cachedParticipations.
     *
     * If the result does not contain a participation ID or the participation is not cached,
     * the method returns without modifying anything.
     *
     * @param result The new result that should be reflected in the cached participation state.
     */
    private updateCachedParticipationWithResult = (result: Result): void => {
        const participationId = result.submission?.participation?.id;
        if (!participationId) {
            return;
        }

        const cachedParticipation = this.cachedParticipations.get(participationId);
        if (!cachedParticipation) {
            return;
        }

        // load submissions of given participation and remove stale websocket-only Athena status entries
        const submissions = this.removeStaleAthenaPlaceholders(cachedParticipation.submissions ?? [], result);

        // if submission with latest result exists, append result to submission
        const { updatedSubmissions, hasMatchingSubmission } = this.updateExistingSubmissionResult(submissions, result);
        // if not then create a new submission object with result appended
        const appendedOrUpdatedSubmission = hasMatchingSubmission ? updatedSubmissions : this.appendNewSubmission(updatedSubmissions, cachedParticipation, result);

        const updatedParticipation = deepClone(cachedParticipation);
        if (result.submission?.participation?.initializationState !== undefined) {
            updatedParticipation.initializationState = result.submission.participation.initializationState;
        }
        updatedParticipation.submissions = appendedOrUpdatedSubmission;
        this.cachedParticipations.set(participationId, updatedParticipation);
    };

    /**
     * Appends a synthetic submission for a result whose submission is not yet
     * present in the cached participation.
     *
     * Uses result.submission as a base, fixes the participation reference, and
     * ensures the new result appears exactly once in the submission's results array.
     *
     * @param submissions Current list of submissions for the participation
     * @param result The new result to apply
     * @returns An object containing:
     *          - submissionsWithMatch: the updated submissions array
     *          - matchedExistingSubmission: whether a submission was updated
     */
    private updateExistingSubmissionResult(submissions: Submission[], result: Result): SubmissionUpdateResult {
        let matchedExistingSubmission = false;

        const submissionsAfterUpdate: Submission[] = submissions.map((submission) => {
            if (submission.id !== result.submission!.id) {
                return submission;
            }

            matchedExistingSubmission = true;

            const existingResults = (submission.results ?? []).filter((existingResult) => !this.shouldReplaceExistingResult(existingResult, result));
            // we do not mutate the mergedResults, hence concat is fine
            const mergedResults = existingResults.concat(result);

            const submissionToUpdate = deepClone(submission);
            submissionToUpdate.results = mergedResults;

            return submissionToUpdate;
        });

        return { updatedSubmissions: submissionsAfterUpdate, hasMatchingSubmission: matchedExistingSubmission };
    }

    private shouldReplaceExistingResult(existingResult: Result, incomingResult: Result): boolean {
        if (existingResult.id === incomingResult.id) {
            return true;
        }

        return this.isUnfinishedAthenaPlaceholder(existingResult) && this.isPersistedAthenaResult(incomingResult);
    }

    private removeStaleAthenaPlaceholders(submissions: Submission[], incomingResult: Result): Submission[] {
        if (!this.isPersistedAthenaResult(incomingResult)) {
            return submissions;
        }

        return submissions.map((submission) => {
            const results = submission.results ?? [];
            const persistentOrFinishedResults = results.filter((result) => !this.isUnfinishedAthenaPlaceholder(result));
            if (persistentOrFinishedResults.length === results.length) {
                return submission;
            }

            const updatedSubmission = deepClone(submission);
            updatedSubmission.results = persistentOrFinishedResults;
            return updatedSubmission;
        });
    }

    private isUnfinishedAthenaPlaceholder(result: Result): boolean {
        return result.id === undefined && result.assessmentType === AssessmentType.AUTOMATIC_ATHENA && result.successful !== true;
    }

    private isPersistedAthenaResult(result: Result): boolean {
        return result.id !== undefined && result.assessmentType === AssessmentType.AUTOMATIC_ATHENA;
    }

    /**
     * Creates and appends a new submission for a result whose submission
     * is not yet present on the cached participation.
     *
     * The new submission is based on result.submission and linked to the given participation, and
     * contains a deduplicated results list that includes the incoming result.
     *
     * @param submissions existing submissions of the participation
     * @param participation participation to which the new submission belongs
     * @param result incoming result that triggered creation of the submission
     */
    private appendNewSubmission(submissions: Submission[], participation: StudentParticipation, result: Result): Submission[] {
        const baseResults = result.submission?.results ?? [];
        // we do not mutate the mergedResults, hence concat is fine
        const mergedResults = baseResults.concat(result);

        const deduplicatedResults = mergedResults.filter((mergedResult, index, allResults) => {
            if (mergedResult.id == null) {
                // For results without an ID, we keep all of them
                return true;
            }
            return allResults.findIndex((result) => result.id === mergedResult.id) === index;
        });

        const newSubmission = deepClone(result.submission as Submission);
        newSubmission.participation = participation;
        newSubmission.results = deduplicatedResults;
        // We only need to append the new submission to the submissions array, hence no deepClone required
        return submissions.concat(newSubmission);
    }

    /**
     * Notifies the result and participation subscribers with the newest result.
     * Note: the result must contain the participation id.
     *
     * @param result The result with which the subscribers get notified
     */
    public notifyAllResultSubscribers = (result: Result) => {
        of(result).pipe(this.getNotifyAllSubscribersPipe()).subscribe();
    };

    /**
     * Subscribes for general changes in a participation object.
     * This will be triggered if a new result is received by the service.
     * A received object will be the full participation object including all results and the exercise.
     *
     * If no observable exists a new one will be created.
     *
     * @returns A BehaviorSubject emitting participation updates, starting with undefined
     */
    public subscribeForParticipationChanges(): BehaviorSubject<Participation | undefined> {
        if (!this.participationObservable) {
            this.participationObservable = new BehaviorSubject<Participation | undefined>(undefined);
        }
        return this.participationObservable;
    }

    /**
     * Subscribes to new results of a certain participation.
     * This will be triggered if a new result is received by the service.
     * A received object will be a result object.
     *
     * If there is no observable for the participation, a new one will be created.
     *
     * @param participationId Id of Participation of which result to subscribe to
     * @param personal Whether the current user is a participant in the participation
     * @param exerciseId Optional exerciseId of the exercise where the participation is part of,
     *                   only needed if personal == false
     * @returns BehaviorSubject that emits the latest result or undefined initially
     */
    public subscribeForLatestResultOfParticipation(participationId: number, personal: boolean, exerciseId?: number): BehaviorSubject<Result | undefined> {
        this.openResultWebsocketSubscriptionIfNotExisting(participationId, personal, exerciseId);
        let resultObservable = this.resultObservables.get(participationId)!;
        if (!resultObservable) {
            resultObservable = new BehaviorSubject<Result | undefined>(undefined);
            this.resultObservables.set(participationId, resultObservable);
        }
        return resultObservable;
    }

    /**
     * Unsubscribe from tracking the latest result of a participation.
     * Might also remove the underlying websocket subscription if the exercise is no longer active.
     *
     * @param participationId ID of the participation
     * @param exercise The exercise to which the participationId belongs.
     *                 Needed for deciding whether to unsubscribe from the websocket.
     */
    public unsubscribeForLatestResultOfParticipation(participationId: number, exercise: Exercise): void {
        // Only unsubscribe from websocket, if the exercise is not active any more
        let isInactiveProgrammingExercise = false;
        if (exercise.type === ExerciseType.PROGRAMMING) {
            const programmingExercise = exercise as ProgrammingExercise;
            isInactiveProgrammingExercise =
                !!programmingExercise.buildAndTestStudentSubmissionsAfterDueDate && dayjs(programmingExercise.buildAndTestStudentSubmissionsAfterDueDate).isBefore(dayjs());
        }
        if (isInactiveProgrammingExercise || (exercise.dueDate && dayjs(exercise.dueDate).isBefore(dayjs()))) {
            this.removeParticipation(participationId, exercise.id);
        }
    }
}
